import diff from './diff';

export default function fastSetStyle(
    map: mapboxgl.Map,
    prevStyle: mapboxgl.Style | undefined,
    style: mapboxgl.Style,
) {
    // @ts-ignore There is no way to access this value publicly even though it's an error to use
    // certain APIs when it's false.
    const isStyleLoaded = map.style && map.style._loaded;

    if (!prevStyle || !style || !isStyleLoaded) {
        return map.setStyle(style);
    }

    const ops = diff(prevStyle, style) as DiffOperation[];

    for (const op of ops) {
        if (op.command === 'addLayer') {
            map.addLayer.apply(map, op.args);

        } else if (op.command === 'removeLayer') {
            map.removeLayer.apply(map, op.args);

        } else if (op.command === 'setPaintProperty') {
            // @ts-ignore The type definition for Mapbox GL JS does not include the options object
            map.setPaintProperty(op.args[0], op.args[1], op.args[2], {validate: false});

        } else if (op.command === 'setLayoutProperty') {
            // @ts-ignore The type definition for Mapbox GL JS does not include the options object
            map.setLayoutProperty(op.args[0], op.args[1], op.args[2], {validate: false});

        } else if (op.command === 'setFilter') {
            // @ts-ignore The type definition for Mapbox GL JS does not include the options object
            map.setFilter(op.args[0], op.args[1], {validate: false});

        } else if (op.command === 'addSource') {
            map.addSource.apply(map, op.args);

        } else if (op.command === 'removeSource') {
            map.removeSource.apply(map, op.args);

        } else if (op.command === 'setLayerZoomRange') {
            map.setLayerZoomRange.apply(map, op.args);

        } else if (op.command === 'setLight') {
            map.setLight(op.args[0], {validate: false});

        } else if (op.command === 'setGeoJSONSourceData') {
            queueGeoJSONSourceData(map, op.args[0], op.args[1]);
            flushGeoJSONSourceData(map);

        } else if (
            op.command === 'setCenter' ||
            op.command === 'setZoom' ||
            op.command === 'setBearing' ||
            op.command === 'setPitch' ||
            op.command === 'setTransition'
        ) {
            continue;

        } else {
            return map.setStyle(style, {diff: false});
        }
    }
}

const dataQueue = new Map<mapboxgl.Map, Record<string, GeoJSONData>>();

function flushGeoJSONSourceData(map: mapboxgl.Map) {
    const mapDataQueue = dataQueue.get(map);
    if (!mapDataQueue) return;
    for (const id in mapDataQueue) {
        const source = map.getSource(id);
        if (source && source.type === 'geojson' && map.isSourceLoaded(id)) {
            source.setData(mapDataQueue[id]);
            delete mapDataQueue[id];
        }
    }
}

function queueGeoJSONSourceData(map: mapboxgl.Map, id: string, data: GeoJSONData) {
    if (!dataQueue.get(map)) {
        dataQueue.set(map, {});
        map.on('data', () => flushGeoJSONSourceData(map));
    }
    dataQueue.get(map)![id] = data;
}

// https://github.com/mapbox/mapbox-gl-js/blob/master/src/style-spec/diff.js
type GeoJSONData = (
    GeoJSON.Feature<GeoJSON.Geometry> |
    GeoJSON.FeatureCollection<GeoJSON.Geometry> |
    string
);

// These types aim for consistency with those defined in
// https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/mapbox-gl/index.d.ts
type DiffOperation = (
    {command: 'setStyle'; args: [mapboxgl.Style]} |
    {command: 'addLayer'; args: [mapboxgl.Layer | mapboxgl.CustomLayerInterface, string?]} |
    {command: 'removeLayer'; args: [string]} |
    {command: 'setPaintProperty'; args: [string, string, any]} |
    {command: 'setLayoutProperty'; args: [string, string, any]} |
    {command: 'setFilter'; args: [string, any[]]} |
    {command: 'addSource'; args: [string, mapboxgl.AnySourceData]} |
    {command: 'removeSource'; args: [string]} |
    {command: 'setGeoJSONSourceData'; args: [string, GeoJSONData]} |
    {command: 'setLayerZoomRange'; args: [string, number, number]} |
    {command: 'setLayerProperty'; args: [string, string, any]} |
    {command: 'setCenter'; args: [[number, number]]} |
    {command: 'setZoom'; args: [number]} |
    {command: 'setBearing'; args: [number]} |
    {command: 'setPitch'; args: [number]} |
    {command: 'setSprite'; args: [string]} |
    {command: 'setGlyphs'; args: [string]} |
    {command: 'setTransition'; args: [mapboxgl.Transition]} |
    {command: 'setLight'; args: [mapboxgl.Light]}
);
